/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';
import {faIdCard} from '@fortawesome/free-solid-svg-icons';

import {
  Alert,
  ContentBody,
  FormUtils,
  Page,
  PageHeader,
  PageTitle,
  Section,
  Select
} from '@sonatype/nexus-ui-plugin';

import {
  NxButton,
  NxForm,
  NxFormGroup,
  NxTooltip,
} from '@sonatype/react-shared-components';

import SamlConfigurationMachine from './SamlConfigurationMachine';
import UIStrings from '../../../../constants/UIStrings';

import './SamlConfiguration.scss';
import {NxTextInput} from "@sonatype/react-shared-components";

const FIELDS = UIStrings.SAML_CONFIGURATION.FIELDS;

export default function SamlConfiguration() {
  const [current, send] = useMachine(SamlConfigurationMachine, {devTools: true});

  const {data : settings, isPristine, validationErrors, saveError, loadError} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = FormUtils.isInvalid(validationErrors);
  const errorMessage = saveError;

  function save() {
    send('SAVE');
  }

  function discard() {
    send('RESET');
  }

  function retry() {
    send('RETRY');
  }

  let error = null;
  if (errorMessage instanceof Array) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}
      <ul>
        {errorMessage.map(error => <li key={error.id}>{JSON.stringify(error)}</li>)}
      </ul>
    </Alert>;
  }
  else if (errorMessage) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}<br/>
      {errorMessage}
    </Alert>;
  }

  return <Page>
    <PageHeader><PageTitle icon={faIdCard} {...UIStrings.SAML_CONFIGURATION.MENU}/></PageHeader>
    <ContentBody className='nxrm-saml-configuration'>
      <Section>
        <NxForm
            loading={isLoading}
            loadError={loadError}
            doLoad={retry}
            onSubmit={save}
            submitError={saveError}
            submitMaskState={isSaving ? false : null}
            submitBtnText={UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
            validationErrors={FormUtils.saveTooltip({isPristine, isInvalid})}
            additionalFooterBtns={
              <NxTooltip title={FormUtils.discardTooltip({isPristine})}>
                <NxButton type="button" className={isPristine && 'disabled'} onClick={discard}>
                  {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
                </NxButton>
              </NxTooltip>
            }>
          {error}
          <NxFormGroup label={FIELDS.idpMetadataLabel} isRequired>
            <NxTextInput
                type="textarea"
                className="nx-text-input--long"
                {...FormUtils.fieldProps('idpMetadata', current)}
                onChange={FormUtils.handleUpdate('idpMetadata', send)}/>
          </NxFormGroup>
          <NxFormGroup label={FIELDS.entityIdUriLabel} sublabel={FIELDS.entityIdUriDescription} isRequired>
            <NxTextInput
                className="nx-text-input--long"
                {...FormUtils.fieldProps('entityIdUri', current)}
                onChange={FormUtils.handleUpdate('entityIdUri', send)}/>
          </NxFormGroup>
          <NxFormGroup
              label={FIELDS.validateResponseSignatureLabel}
              sublabel={FIELDS.validateResponseSignatureDescription}
              isRequired>
            <Select
                name="validateResponseSignature"
                value={settings.validateResponseSignature}
                onChange={FormUtils.handleUpdate('validateResponseSignature', send)}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </NxFormGroup>
          <NxFormGroup
              label={FIELDS.validateAssertionSignatureLabel}
              sublabel={FIELDS.validateAssertionSignatureDescription}
              isRequired>
            <Select
                name="validateAssertionSignature"
                value={settings.validateAssertionSignature}
                onChange={FormUtils.handleUpdate('validateAssertionSignature', send)}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </NxFormGroup>
          <fieldset>
            <legend>IdP Field Mappings</legend>
            <NxFormGroup label={FIELDS.usernameAttrLabel} isRequired>
              <NxTextInput
                  {...FormUtils.fieldProps('usernameAttr', current)}
                  onChange={FormUtils.handleUpdate('usernameAttr', send)}/>
            </NxFormGroup>
            <NxFormGroup label={FIELDS.firstNameAttrLabel}>
              <NxTextInput
                  {...FormUtils.fieldProps('firstNameAttr', current)}
                  onChange={FormUtils.handleUpdate('firstNameAttr', send)}/>
            </NxFormGroup>
            <NxFormGroup label={FIELDS.lastNameAttrLabel}>
              <NxTextInput
                  {...FormUtils.fieldProps('lastNameAttr', current)}
                  onChange={FormUtils.handleUpdate('lastNameAttr', send)}/>
            </NxFormGroup>
            <NxFormGroup label={FIELDS.emailAttrLabel}>
              <NxTextInput
                  {...FormUtils.fieldProps('emailAttr', current)}
                  onChange={FormUtils.handleUpdate('emailAttr', send)}/>
            </NxFormGroup>
            <NxFormGroup label={FIELDS.roleAttrLabel}>
              <NxTextInput
                  {...FormUtils.fieldProps('roleAttr', current)}
                  onChange={FormUtils.handleUpdate('roleAttr', send)}/>
            </NxFormGroup>
          </fieldset>
        </NxForm>
      </Section>
    </ContentBody>
  </Page>;
}
